import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { useAppDispatch, useAppSelector } from '../../../../../hooks/redux';
import {
  publishTournamentScheduleAction,
  updateTournamentScheduleAction,
} from '../../../../../features/schedule/scheduleActions';
import {
  selectScheduleData,
  selectSchedulePublishStatus,
  selectSchedulePublishing,
  selectScheduleTournamentInfo,
  selectScheduleUpdating,
} from '../../../../../features/schedule/scheduleSelectors';
import { getTournamentTimezone } from '../schedule.helpers';

const prefix = 'creativeDetail-';

export function useScheduleSavePublish() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const schedule = useAppSelector(selectScheduleData);
  const publishStatus = useAppSelector(selectSchedulePublishStatus);
  const updatingSchedule = useAppSelector(selectScheduleUpdating);
  const publishingSchedule = useAppSelector(selectSchedulePublishing);
  const tournamentInfo = useAppSelector(selectScheduleTournamentInfo);

  const scheduleTimeZone = getTournamentTimezone(
    tournamentInfo?.country,
    tournamentInfo?.timeZone
  );

  const formatTimestamp = useCallback(
    (iso: string | null) => {
      if (!iso) return null;
      return formatInTimeZone(new Date(iso), scheduleTimeZone, 'yyyy-MM-dd HH:mm');
    },
    [scheduleTimeZone]
  );

  const chipTimeLabel = useMemo(() => {
    if (publishStatus.variant === 'warning' && publishStatus.draftSavedAt) {
      const time = formatTimestamp(publishStatus.draftSavedAt);
      return time ? t(prefix + 'scheduleStatusDraftSavedAt', { time }) : null;
    }
    if (publishStatus.variant === 'success' && publishStatus.publishedAt) {
      const time = formatTimestamp(publishStatus.publishedAt);
      return time ? t(prefix + 'scheduleStatusPublishedAt', { time }) : null;
    }
    return null;
  }, [formatTimestamp, publishStatus, t]);

  const chipLabel = useMemo(() => {
    if (!publishStatus.labelKey) return '';
    return t(publishStatus.labelKey);
  }, [publishStatus.labelKey, t]);

  const actionsDisabled = !schedule || updatingSchedule || publishingSchedule;

  const saveDraftSchedule = useCallback(() => {
    if (!schedule) return;
    dispatch(updateTournamentScheduleAction(schedule, t));
  }, [dispatch, schedule, t]);

  const publishSchedule = useCallback(() => {
    if (!schedule) return;
    dispatch(publishTournamentScheduleAction(schedule, t));
  }, [dispatch, schedule, t]);

  return {
    publishStatus,
    chipLabel,
    chipTimeLabel,
    actionsDisabled,
    updatingSchedule,
    publishingSchedule,
    saveDraftSchedule,
    publishSchedule,
  };
}
